// Key derivation (docs/01-architecture.md section 9 with the R1 resolution):
// mnemonic -> BIP-39 seed -> master (k, c) -> per-tree seed bound to the
// program id -> chain starts, public parameter and signing salts from a
// Keccak-256 PRF -> the verifier package's fromSecrets.
//
// Byte-for-byte the Rust module sdk/rust/src/derive.rs; the golden vector
// sdk/fixtures/derivation-v1.json pins both (test/derivation.test.ts).
// Signing is deterministic: the salt for trial n is a PRF of
// (seed_t, leaf, digest, n), so the same (leaf, digest) always yields the
// same bytes and an exact retry is a byte-identical signature, not a reuse.

import { hmac } from '@noble/hashes/hmac.js';
import { sha512 } from '@noble/hashes/sha2.js';
import { keccak_256 } from '@noble/hashes/sha3.js';
import { mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import { SecretKey } from '../vendor/solana-winternitz/src/hazmat/xmss.js';
import type { Signature } from '../vendor/solana-winternitz/src/xmss.js';

/** The English BIP-39 wordlist, for the app's per-word validation and autocomplete on restore (docs/08 5.2). */
export const WORDLIST: readonly string[] = wordlist;

/** HMAC key of the master derivation (section 9 step 2). */
export const MASTER_LABEL: Uint8Array = new TextEncoder().encode('THEVAULT seed');
/** Domain separator of the Keccak PRF (section 9 step 4, hash-sig ShaPRF layout). */
export const SEP16: Uint8Array = Uint8Array.of(0x00, 0x01, 0x12, 0xff, 0x00, 0x01, 0xfa, 0xff, 0x00, 0xaf, 0x12, 0xff, 0x01, 0xfa, 0xff, 0x00);
export const PRF_CHAIN = 0x00;
export const PRF_SALT = 0x01;
export const PRF_PARAMETER = 0x02;
/** Winternitz chains per leaf, bytes per chain element and salt (DKKW25 parameters of the verifier). */
export const CHAINS = 36;
export const ELEMENT_LEN = 23;
export const SALT_LEN = 21;
export const PARAMETER_LEN = 18;
export const LEAVES = 256;
/** Chain starts of one tree: leaf-major, chain-major, 828 bytes per leaf. */
export const SECRETS_LEN = LEAVES * CHAINS * ELEMENT_LEN;
/** Tree indices are below 2^31 so the hardened bit never wraps (R1, R6). */
export const MAX_TREE_INDEX = 2 ** 31;

export type DeriveErrorCode = 'Mnemonic' | 'Seed' | 'ProgramId' | 'TreeIndexTooLarge' | 'Digest' | 'ClusterTag' | 'FeeGenerationTooLarge';

export class DeriveError extends Error {
  constructor(readonly code: DeriveErrorCode, message: string) {
    super(message);
    this.name = 'DeriveError';
  }
}

export interface Master {
  readonly k: Uint8Array;
  readonly c: Uint8Array;
}

/** One tree's secret material: the seed, the chain starts and P. Call `wipe` when done. */
export interface TreeSecrets {
  readonly seed: Uint8Array;
  readonly secrets: Uint8Array;
  readonly parameter: Uint8Array;
}

export function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

export function u32be(n: number): Uint8Array {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, n >>> 0, false);
  return out;
}

export function u64be(n: number | bigint): Uint8Array {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, BigInt(n), false);
  return out;
}

/**
 * BIP-39 seed (64 bytes) of a checksum-valid English mnemonic. Mnemonic and
 * passphrase are NFKD-normalised here, the same boundary as the Rust client,
 * so composed and decomposed spellings derive one seed (R15). A non-empty
 * passphrase is part of the recovery identity: the phrase alone does not
 * regenerate the vault.
 */
export function seedFromMnemonic(mnemonic: string, passphrase = ''): Uint8Array {
  const m = mnemonic.normalize('NFKD');
  if (!validateMnemonic(m, wordlist)) throw new DeriveError('Mnemonic', 'not a checksum-valid English BIP-39 mnemonic');
  return mnemonicToSeedSync(m, passphrase.normalize('NFKD'));
}

/** (k, c) = HMAC-SHA512(key = "THEVAULT seed", seed64). */
export function master(seed64: Uint8Array): Master {
  if (seed64.length !== 64) throw new DeriveError('Seed', 'seed must be 64 bytes');
  const out = hmac(sha512, MASTER_LABEL, seed64);
  return { k: out.slice(0, 32), c: out.slice(32, 64) };
}

/** seed_t = HMAC-SHA512(key = c, 0x00 || k || program_id || u32be(t | 0x80000000))[0..32]; t < 2^31 (R1). */
export function treeSeed(m: Master, programId: Uint8Array, t: number): Uint8Array {
  if (programId.length !== 32) throw new DeriveError('ProgramId', 'program id must be 32 bytes');
  if (!Number.isInteger(t) || t < 0 || t >= MAX_TREE_INDEX) throw new DeriveError('TreeIndexTooLarge', `tree index ${t} is not in 0..2^31`);
  const hardened = u32be((t | 0x80000000) >>> 0);
  return hmac(sha512, m.c, concat(Uint8Array.of(0x00), m.k, programId, hardened)).slice(0, 32);
}

/** keccak256(SEP16 || role || seed_t || tail...). */
function prf(seedT: Uint8Array, role: number, ...tail: Uint8Array[]): Uint8Array {
  return keccak_256(concat(SEP16, Uint8Array.of(role), seedT, ...tail));
}

/** chain_start[leaf][i] = PRF(0x00, leaf u32be || i u64be)[0..23]. */
export function chainStart(seedT: Uint8Array, leaf: number, chain: number): Uint8Array {
  return prf(seedT, PRF_CHAIN, u32be(leaf), u64be(chain)).slice(0, ELEMENT_LEN);
}

/** P = PRF(0x02)[0..18]: the public parameter, derived rather than sampled. */
export function parameter(seedT: Uint8Array): Uint8Array {
  return prf(seedT, PRF_PARAMETER).slice(0, PARAMETER_LEN);
}

/** salt(leaf, digest, n) = PRF(0x01, leaf u32be || digest || n u64be)[0..21]: the salt tried at trial n. */
export function salt(seedT: Uint8Array, leaf: number, digest: Uint8Array, trial: number): Uint8Array {
  return prf(seedT, PRF_SALT, u32be(leaf), digest, u64be(trial)).slice(0, SALT_LEN);
}

export function treeSecrets(seedT: Uint8Array): TreeSecrets {
  if (seedT.length !== 32) throw new DeriveError('Seed', 'tree seed must be 32 bytes');
  const secrets = new Uint8Array(SECRETS_LEN);
  for (let leaf = 0; leaf < LEAVES; leaf++) {
    for (let i = 0; i < CHAINS; i++) secrets.set(chainStart(seedT, leaf, i), (leaf * CHAINS + i) * ELEMENT_LEN);
  }
  return { seed: seedT.slice(), secrets, parameter: parameter(seedT) };
}

/** Build the key (the verifier's fromSecrets: 256 leaf hashes and the Merkle tree). */
export function treeKey(ts: TreeSecrets): SecretKey {
  return SecretKey.fromSecrets(ts.secrets, ts.parameter);
}

/** Tree t of master m under programId. */
export function deriveTree(m: Master, programId: Uint8Array, t: number): TreeSecrets {
  return treeSecrets(treeSeed(m, programId, t));
}

/**
 * Sign digest at leaf with the deterministic salt sequence. Returns the
 * signature and the trial index of the accepted salt. Identical inputs give
 * identical bytes; the caller still owns leaf allocation (one digest per leaf).
 */
export function signDeterministic(key: SecretKey, seedT: Uint8Array, leaf: number, digest: Uint8Array): { signature: Signature; saltTrial: number } {
  if (digest.length !== 32) throw new DeriveError('Digest', 'digest must be 32 bytes');
  let trials = 0;
  const signature = key.signAt(leaf, digest, (buf) => {
    if (buf.length !== SALT_LEN) throw new Error(`salt request of ${buf.length} bytes`);
    buf.set(salt(seedT, leaf, digest, trials));
    trials += 1;
  });
  return { signature, saltTrial: trials - 1 };
}

/** Zero secret buffers when they are no longer needed. */
export function wipe(...buffers: Uint8Array[]): void {
  for (const b of buffers) b.fill(0);
}

// ------------------------------------------------------------------ the fee branch (docs/18-one-fee-reserve.md section 2)
//
// One Ed25519 fee key per vault, derived from the same master: the fee reserve
// (the gas wallet) is the same address on every device that holds the words.
// The message starts with 0x01 where every tree message starts with 0x00, and
// is 116 bytes against the tree message's 69, so no fee message ever equals a
// tree message; the two outputs are as unrelated as two tree seeds are, under
// the PRF assumption on HMAC-SHA512 the tree seeds already rest on. The
// program id and the cluster tag come from one approved deployment entry
// (clusters.ts DEPLOYMENTS), compiled into the worker, never from an RPC
// answer. Byte for byte sdk/rust/src/derive.rs `fee_seed`; the golden vector
// sdk/fixtures/fee-derivation-v1.json pins both (test/fee-derivation.test.ts).

/** The fee branch's versioned label; its length byte (0x0e, 14) precedes it in the message, so the encoding stays injective under any later label. */
export const FEE_LABEL: Uint8Array = new TextEncoder().encode('THEVAULT/FEE/1');
/** Fee generations are below 2^31 so the hardened bit never wraps; this release derives generation 0 only (docs/18 2.3). */
export const MAX_FEE_GENERATION = 2 ** 31;
/** The fee message's length: 1 + 32 + 1 + 14 + 32 + 32 + 4. */
export const FEE_MESSAGE_LEN = 116;

/**
 * The 116-byte fee message: `0x01 || k || 0x0e || "THEVAULT/FEE/1" || program_id || cluster_tag || u32be(n | 0x80000000)`.
 * It carries k: the caller zeroes it. Exposed for the fixture; `feeSeed` is the derivation.
 */
export function feeMessage(m: Master, programId: Uint8Array, clusterTag: Uint8Array, n: number): Uint8Array {
  if (m.k.length !== 32 || m.c.length !== 32) throw new DeriveError('Seed', 'master k and c must each be 32 bytes');
  if (programId.length !== 32) throw new DeriveError('ProgramId', 'program id must be 32 bytes');
  if (clusterTag.length !== 32) throw new DeriveError('ClusterTag', 'cluster tag must be 32 bytes');
  if (!Number.isInteger(n) || n < 0 || n >= MAX_FEE_GENERATION) throw new DeriveError('FeeGenerationTooLarge', `fee generation ${n} is not in 0..2^31`);
  const message = concat(Uint8Array.of(0x01), m.k, Uint8Array.of(FEE_LABEL.length), FEE_LABEL, programId, clusterTag, u32be((n | 0x80000000) >>> 0));
  if (message.length !== FEE_MESSAGE_LEN) throw new DeriveError('Seed', `fee message is ${message.length} bytes, not ${FEE_MESSAGE_LEN}`);
  return message;
}

/**
 * `fee_seed(n) = HMAC-SHA512(key = c, feeMessage(n))[0..32]`: the 32-byte RFC 8032 private seed of fee key n
 * (docs/18 section 2). The message (it carries k) and the 64-byte HMAC output are zeroed here, and noble's
 * one-shot `hmac` destroys its own state; anything else is best effort, as with `treeSeed`. The caller owns
 * and zeroes the returned seed.
 */
export function feeSeed(m: Master, programId: Uint8Array, clusterTag: Uint8Array, n: number): Uint8Array {
  const message = feeMessage(m, programId, clusterTag, n);
  let full: Uint8Array | undefined;
  try {
    full = hmac(sha512, m.c, message);
    return full.slice(0, 32);
  } finally {
    message.fill(0);
    full?.fill(0);
  }
}
